import React, { useState } from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import AccessPointZonesSection from './AccessPointZonesSection'
import CodesSection from './CodesSection'
import EventWifiSection from './EventWifiSection'
import KeyFigures from './KeyFigures'
import MobileDevicesSection from './MobileDevicesSection'
import MobilePrivateWifiSection from './MobilePrivateWifiSection'
import PrivateWifiSection from './PrivateWifiSection'
import StreamCastSection from './StreamCastSection'
import UsageSection from './UsageSection'
import LimitsNotice from './components/LimitsNotice'
import PeriodSelector from './components/PeriodSelector'
import PlatformSelector from './components/PlatformSelector'
import { DEFAULT_PERIOD, DEFAULT_PLATFORM } from './constants'
import { PALETTE } from './styles'
import { ProductAnalyticsPeriod, ProductAnalyticsPlatform } from './types'

type PlatformSectionsProps = {
  period: ProductAnalyticsPeriod
}

const WebSections: React.FC<PlatformSectionsProps> = ({ period }) => (
  <>
    <KeyFigures period={period} platform="WEB" />
    <UsageSection period={period} platform="WEB" />
    <CodesSection period={period} platform="WEB" />
    <PrivateWifiSection period={period} />
    <EventWifiSection period={period} />
    <AccessPointZonesSection period={period} />
    <StreamCastSection period={period} />
  </>
)

const MobileSections: React.FC<PlatformSectionsProps> = ({ period }) => (
  <>
    <KeyFigures period={period} platform="MOBILE" />
    <UsageSection period={period} platform="MOBILE" />
    <MobileDevicesSection period={period} />
    <CodesSection period={period} platform="MOBILE" />
    <MobilePrivateWifiSection period={period} />
  </>
)

const ProductAnalytics: React.FC = () => {
  const { t } = useTranslation()
  const [period, setPeriod] = useState<ProductAnalyticsPeriod>(DEFAULT_PERIOD)
  const [platform, setPlatform] = useState<ProductAnalyticsPlatform>(DEFAULT_PLATFORM)

  const handlePeriodChange = (nextPeriod: ProductAnalyticsPeriod) => setPeriod(nextPeriod)
  const handlePlatformChange = (nextPlatform: ProductAnalyticsPlatform) => setPlatform(nextPlatform)

  return (
    <Stack gap={2.5}>
      <Stack
        component="header"
        direction="row"
        flexWrap="wrap"
        alignItems="flex-end"
        justifyContent="space-between"
        gap={1.5}
      >
        <Stack gap={0.5}>
          <Typography component="h1" sx={{ fontSize: 16, fontWeight: 600, color: PALETTE.text }}>
            {t('stats.product_analytics.title')}
          </Typography>
          <Typography sx={{ fontSize: 12, color: PALETTE.muted }}>{t('stats.product_analytics.subtitle')}</Typography>
        </Stack>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <PlatformSelector value={platform} onChange={handlePlatformChange} />
          <PeriodSelector value={period} onChange={handlePeriodChange} />
        </Stack>
      </Stack>
      <LimitsNotice platform={platform} />
      {platform === 'MOBILE' ? <MobileSections period={period} /> : <WebSections period={period} />}
    </Stack>
  )
}

export default ProductAnalytics
